import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import Joi from 'joi';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Blockquote, FormRow } from '~/components/ui';
import { useApi } from '../../../hooks/useApi';
import { ClubMember } from '../types';

interface Props {
    member: ClubMember;
    /** receives the refreshed member row returned by the API */
    onSuccess?: (member: ClubMember) => void;
    callback?: () => void;
}

/**
 * Suspends a member either open ended or for a defined period. The period may
 * start in the future — the backend derives the effective state from the dates,
 * so no job has to run for it to take effect.
 */
export const SuspendClubMemberForm = ({ member, onSuccess, callback }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);

    // memoized: the shared Form re-seeds every field whenever this object changes identity, and
    // the loading state alone re-renders it — an inline object put the start date back to today
    // right when the overlap error came in, taking the rejected date off screen with it
    const defaultValues = useMemo(() => ({ starts_at: moment().toDate(), ends_at: null, notify_member: false }), []);

    // sends dates as plain YYYY-MM-DD, the backend stores them as date columns
    const handleSubmit = (data: any) => {
        setIsLoading(true);
        return api({
            url: `club-member/${member.id}/suspension`,
            method: 'POST',
            data: {
                starts_at: data.starts_at ? moment(data.starts_at).format('YYYY-MM-DD') : undefined,
                ends_at: data.ends_at ? moment(data.ends_at).format('YYYY-MM-DD') : undefined,
                reason: data.reason || undefined,
                notify_member: !!data.notify_member
            },
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) => {
                onSuccess?.(data);
                callback?.();
            }
        });
    };

    return (
        <>
            <Blockquote>{t('clubMember.suspension.form.hint')}</Blockquote>
            <Form
                onSubmit={handleSubmit}
                defaultValues={defaultValues}
                validationSchema={Joi.object({
                    starts_at: Joi.any(),
                    ends_at: Joi.any(),
                    reason: Joi.string().allow('', null).max(2000).label(t('clubMember.suspension.form.reason')),
                    notify_member: Joi.boolean()
                })}
            >
                <FormRow>
                    <FormInput.DatePicker
                        name="starts_at"
                        label={t('clubMember.suspension.form.startsAt')}
                        helperText={t('clubMember.suspension.form.startsAtHelp')}
                    />
                    <FormInput.DatePicker
                        name="ends_at"
                        label={t('clubMember.suspension.form.endsAt')}
                        helperText={t('clubMember.suspension.form.endsAtHelp')}
                    />
                </FormRow>
                <FormInput.Textarea name="reason" label={t('clubMember.suspension.form.reason')} />
                <FormInput.Checkbox name="notify_member" label={t('clubMember.suspension.form.notifyMember')} />
                <FormInput.Submit isLoading={isLoading}>{t('clubMember.suspension.form.submit')}</FormInput.Submit>
            </Form>
        </>
    );
};

export default SuspendClubMemberForm;
